import React from 'react';
import { TrendingUp, Zap } from 'lucide-react';
import type { BrokerGrowthRow } from '../../../types/acquisitionAnalysis';

interface BrokerGrowthTrajectoryProps {
  data: BrokerGrowthRow[];
}

export const BrokerGrowthTrajectory: React.FC<BrokerGrowthTrajectoryProps> = ({ data }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const getGrowthColor = (growth: number) => {
    if (growth >= 100) return 'text-emerald-400';
    if (growth >= 50) return 'text-green-400';
    if (growth >= 0) return 'text-blue-400';
    return 'text-red-400';
  };

  const getGrowthBadge = (growth: number) => {
    if (growth >= 100) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
          <Zap className="w-3 h-3" />
          Exceptional
        </span>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-400" />
          Broker Growth Trajectory
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          Brokers with exceptional year-over-year growth (sorted by growth rate)
        </p>
        {/* Get FY labels from data */}
        {(() => {
          const fy1Label = data[0]?.fy1Label || 'FY1';
          const fy2Label = data[0]?.fy2Label || 'FY2';
          return (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Broker
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  {fy1Label}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  {fy2Label}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Growth
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {data.map((broker, index) => (
                <tr key={index} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-white">
                    <div className="flex items-center gap-2">
                      {broker.brokerName}
                      {getGrowthBadge(broker.growth)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-slate-300">
                    {formatCurrency(broker.fy1)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-indigo-400">
                    {formatCurrency(broker.fy2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className={`font-bold ${getGrowthColor(broker.growth)}`}>
                      {formatPercent(broker.growth)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          );
        })()}
      </div>

      {data.some((b) => b.growth >= 100) && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <p className="text-sm text-emerald-300">
            <strong>Exceptional Growth:</strong>{' '}
            {data.filter((b) => b.growth >= 100).length} broker
            {data.filter((b) => b.growth >= 100).length > 1 ? 's have' : ' has'} achieved 100%+
            year-over-year growth. This indicates strong momentum and effective origination
            strategies.
          </p>
        </div>
      )}

      {data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">Average Growth</p>
            <p className="text-2xl font-bold text-indigo-300">
              {formatPercent(data.reduce((sum, b) => sum + b.growth, 0) / data.length)}
            </p>
          </div>
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">Top Growth</p>
            <p className="text-2xl font-bold text-green-300">
              {formatPercent(Math.max(...data.map((b) => b.growth)))}
            </p>
          </div>
          <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">High Performers</p>
            <p className="text-2xl font-bold text-purple-300">
              {data.filter((b) => b.growth >= 50).length}
            </p>
          </div>
        </div>
      )}

      {data.length === 0 && (
        <div className="p-4 bg-slate-700/30 border border-slate-600/30 rounded-lg text-center">
          <p className="text-slate-400">
            No brokers with significant year-over-year growth detected. This may indicate stable individual performance or insufficient data for comparison.
          </p>
        </div>
      )}
    </div>
  );
};
